import {
  HorizontalDivider,
  Icon,
  SegmentedButton,
  Select,
  Typography
} from '@smartgift/frontend-toolkit';
import PropTypes from 'prop-types';
import { useEffect, useMemo, useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';

import { SkeletonLoaders } from '@hero/components';
import { Pagination } from '@hero/components/atoms';
import { UploadImageModal } from '@hero/components/modals';
import { configs } from '@hero/configs';
import { DIGITAL_WRAPPER, WRAPPERS_TAB } from '@hero/constants';
import { useGetCSEntries } from '@hero/services/content-stack-service/hooks';
import { useGetBranding, useUpdateBranding } from '@hero/services/organization-service/hooks';
import { useNotification } from '@hero/store/hooks';

import { ImagePreview } from '../ImagePreview';

import * as S from './style';

const LIMIT = 12;

export function DigitalWrapperSelection({ giftWrapperUrl, onBranding }) {
  const { t } = useTranslation();

  const { showNotification } = useNotification();

  const [featuredPageIndex, setFeaturedPageIndex] = useState(1);
  const [featuredOccasion, setFeaturedOccasion] = useState();

  const { data: brandingData, isFetching: isFetchingBranding } = useGetBranding();
  const { mutateAsync: updateBranding } = useUpdateBranding();

  const { data: dataOccasionsCategories } = useGetCSEntries({
    entryCode: 'sgb_admin_occasion_category',
    params: {
      asc: 'order',
      query: { visibility: true }
    }
  });

  const allOccasionCategoryIds = dataOccasionsCategories?.entries?.map(oc => oc.uid);
  const { data: dataDigitalWrappers, isFetching: isFetchingDigitalWrappers } = useGetCSEntries({
    entryCode: 'sgb_admin_occasion',
    params: {
      pageIndex: featuredPageIndex,
      occasion: featuredOccasion?.value,
      limit: LIMIT,
      query: {
        'occasion_category.uid': {
          $in: featuredOccasion?.value
            ? allOccasionCategoryIds?.filter(ocId => ocId === featuredOccasion?.value)
            : allOccasionCategoryIds
        }
      }
    }
  });
  const occasionOptions = dataOccasionsCategories?.entries?.map(oc => ({
    label: oc.label,
    value: oc.uid
  }));

  const localGiftWrapperUrl = giftWrapperUrl || brandingData?.branding?.giftWrappers?.default;

  const [activeTab, setActiveTab] = useState(WRAPPERS_TAB.featured);

  const [isUploadModalOpen, setIsUploadModalOpen] = useState(false);

  const hasOccasionOptions =
    occasionOptions?.some(option => !!option.label && !!option.value) &&
    activeTab === WRAPPERS_TAB.featured;

  //? Library Wrapper - Start
  const [libraryPageIndex, setLibraryPageIndex] = useState(1);
  const [libraryWrappers, setLibraryWrappers] = useState([]);

  const library = useMemo(() => {
    const brandingLibrary = brandingData?.branding?.giftWrappers?.library || [];
    return [...new Set(brandingLibrary)];
  }, [brandingData]);

  useEffect(() => {
    if (library?.length === 0) return setLibraryWrappers([]);

    let currentDigitalWrappers = library?.slice(
      (libraryPageIndex - 1) * LIMIT,
      libraryPageIndex * LIMIT
    );

    setLibraryWrappers(currentDigitalWrappers);
  }, [library, libraryPageIndex]);

  const onSelect = async url => {
    if (url === localGiftWrapperUrl) return;

    await onBranding({ giftWrapperUrl: url });
  };

  const onDelete = url => {
    showNotification({
      title: t('removeItemPromptQuestion', {
        featureName: t('gift')
      }),
      notificationImage: <Icon name="error" size="xxl" color="var(--color-error-default)" />,
      onSubmit: () => {
        updateBranding({
          branding: { giftWrappers: { library: library.filter(item => item !== url) } }
        });

        onBranding({ giftWrapperUrl: brandingData?.branding?.giftWrappers?.default });
      },
      primaryButtonProps: { color: 'error', children: t('removeItem') },
      secondaryButtonProps: { children: t('keepItem') }
    });
  };

  useEffect(() => {
    if (!!libraryWrappers?.length && !localGiftWrapperUrl) {
      onSelect(libraryWrappers[0]);
    }
  }, [localGiftWrapperUrl, libraryWrappers]);

  const wrapperOptions = [
    {
      value: WRAPPERS_TAB.featured,
      label: t('catalogWrappers'),
      children: (
        <>
          <HorizontalDivider $margin="0 0 12px 0" />
          {hasOccasionOptions &&
            configs?.experience?.digitalWrappersDropdownEnabled &&
            !isFetchingDigitalWrappers && (
              <S.DigitalWrapperFeaturedHeader>
                <Typography variant="body2" color="fg">
                  <Trans
                    i18nKey="giftWraps"
                    count={dataDigitalWrappers?.pagination?.total}
                    components={{ b: <b /> }}
                  />
                </Typography>
                <S.DigitalWrapperSelect>
                  <Typography color="fg" variant="body2">
                    {t('selectOccasion')}:
                  </Typography>
                  <Select
                    placeholder={t('allOccasions')}
                    value={featuredOccasion}
                    size="sm"
                    menuPosition="absolute"
                    options={[{ label: t('allOccasions'), value: '' }, ...occasionOptions]}
                    onChange={selectedOccasion => {
                      setFeaturedOccasion(selectedOccasion);
                      setFeaturedPageIndex(1);
                    }}
                  />
                </S.DigitalWrapperSelect>
              </S.DigitalWrapperFeaturedHeader>
            )}
          <S.ImagePreviewWrapper isFewItems={dataDigitalWrappers?.entries?.length < LIMIT}>
            {isFetchingDigitalWrappers ? (
              <SkeletonLoaders.BrandingLoader count={LIMIT} />
            ) : (
              dataDigitalWrappers?.entries?.map(wrapper => (
                <ImagePreview
                  width={70}
                  src={wrapper.image.url}
                  active={localGiftWrapperUrl === wrapper.image.url}
                  key={wrapper.image.url}
                  isWrapper={true}
                  onClick={() => {
                    onSelect(wrapper.image.url);
                  }}
                />
              ))
            )}
          </S.ImagePreviewWrapper>
          {dataDigitalWrappers?.pagination?.hasPagination && (
            <Pagination
              pageIndex={featuredPageIndex}
              setPageIndex={setFeaturedPageIndex}
              limit={LIMIT}
              totalDocs={dataDigitalWrappers.pagination?.total}
            />
          )}
        </>
      )
    },
    {
      value: WRAPPERS_TAB.saved,
      label: t('myWrappers'),
      children: (
        <>
          <HorizontalDivider $margin="0 0 12px 0" />
          <S.ImagePreviewWrapper isFewItems={libraryWrappers?.length < LIMIT}>
            <S.UploadImageButtonWrapper>
              <S.UploadButton onClick={() => setIsUploadModalOpen(true)}>
                <Icon name="upload" size="md" />
                <Typography variant="body2SemiBold">{t('upload')}</Typography>
              </S.UploadButton>
            </S.UploadImageButtonWrapper>
            {isFetchingBranding ? (
              <SkeletonLoaders.BrandingLoader count={1} />
            ) : (
              libraryWrappers.map(item => (
                <ImagePreview
                  key={item}
                  src={item}
                  active={localGiftWrapperUrl === item}
                  onDelete={() => onDelete(item)}
                  isWrapper={true}
                  showActions={libraryWrappers?.length > 1}
                  width={70}
                  onClick={() => {
                    onSelect(item);
                  }}
                />
              ))
            )}
          </S.ImagePreviewWrapper>
          <Pagination
            pageIndex={libraryPageIndex}
            setPageIndex={setLibraryPageIndex}
            limit={LIMIT}
            totalDocs={libraryWrappers?.length}
          />

          <UploadImageModal
            isOpen={isUploadModalOpen}
            setIsOpen={setIsUploadModalOpen}
            title={t('uploadYourDigitalWrappers')}
            uploadAction={({ cdnUrl } = {}) => {
              updateBranding({
                branding: {
                  giftWrappers: {
                    default: cdnUrl,
                    library: [...new Set([cdnUrl, ...library])]
                  }
                }
              });

              onSelect(cdnUrl);
            }}
          />
        </>
      )
    }
  ];

  return (
    <S.DigitalWrapper id={DIGITAL_WRAPPER}>
      <SegmentedButton
        activeTab={activeTab}
        onChange={setActiveTab}
        updateHash={false}
        options={wrapperOptions}
        size="sm"
        withLine
      />
    </S.DigitalWrapper>
  );
}

DigitalWrapperSelection.propTypes = {
  giftWrapperUrl: PropTypes.string,
  isLoading: PropTypes.bool,
  onBranding: PropTypes.func.isRequired
};
